import React, { useState, useContext, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Autocomplete,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Checkbox,
} from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import { isAuthExpiredDialogVisible, subscribeAuthExpired } from '../../../utils/auth-error-utils';

export default function PatientDetailsDialog({
    open,
    onClose,
    onSubmit,
    clinikoPatient = null,
    clinikoAppointments = [],
    clinikoLoading = false,
}) {
    const { authStore } = useContext(StoreContext);

    const [inputMethod, setInputMethod] = useState('manual'); // 'manual' | 'select' | 'cliniko'
    const [manualPatientName, setManualPatientName] = useState('');
    const [manualPatientSurname, setManualPatientSurname] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedClinikoPatient, setSelectedClinikoPatient] = useState(null);
    const [selectedClinikoAppointment, setSelectedClinikoAppointment] = useState(null);
    const [importClinikoPatient, setImportClinikoPatient] = useState(false);
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const hasClinikoPatient = Boolean(clinikoPatient?.clinikoPatientId);
    const showClinikoOption = hasClinikoPatient;

    useEffect(() => {
        // Fetch patients from API when dialog opens
        let isSubscribed = true;

        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            try {
                const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                    filterType: 'patientStatus',
                    filterValue: 'active',
                });

                if (!isSubscribed) {
                    return;
                }

                if (response?.patients) {
                    setPatients(response.patients);
                    if (response.patients.length === 0) {
                        setInputMethod('manual');
                    }
                } else {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } catch (error) {
                console.error('Failed to fetch patients:', error);
                if (isSubscribed) {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } finally {
                if (isSubscribed) {
                    setIsLoadingPatients(false);
                }
            }
        };

        if (open) {
            fetchPatients();
        }

        return () => {
            isSubscribed = false;
        };
    }, [open, authStore.idToken]);

    useEffect(() => {
        if (!hasClinikoPatient) {
            setSelectedClinikoPatient(null);
            setSelectedClinikoAppointment(null);
            return;
        }
        setSelectedClinikoPatient(clinikoPatient);

        if (Array.isArray(clinikoAppointments) && clinikoAppointments.length === 1) {
            setSelectedClinikoAppointment(clinikoAppointments[0]);
        } else {
            setSelectedClinikoAppointment(null);
        }
    }, [clinikoPatient, clinikoAppointments, hasClinikoPatient]);

    const resetForm = () => {
        setInputMethod('manual');
        setManualPatientName('');
        setManualPatientSurname('');
        setSelectedPatient(null);
        setSelectedClinikoPatient(null);
        setSelectedClinikoAppointment(null);
        setImportClinikoPatient(false);
    };

    const handleSubmit = () => {
        let patientDetails = null;
        let meta = {};

        if (inputMethod === 'manual') {
            if (manualPatientName || manualPatientSurname) {
                patientDetails = {
                    patientName: manualPatientName || null,
                    patientSurname: manualPatientSurname || null,
                };
            }
        } else if (inputMethod === 'select' && selectedPatient) {
            patientDetails = {
                patientName: selectedPatient.patientName || null,
                patientSurname: selectedPatient.patientSurname || null,
                patientId: selectedPatient.patientId || null,
                patientEmail: selectedPatient.patientEmail || null,
                clinicPatientId: selectedPatient.clinicPatientId || null,
            };
        } else if (inputMethod === 'cliniko' && selectedClinikoPatient) {
            patientDetails = {
                patientName: selectedClinikoPatient.firstName || null,
                patientSurname: selectedClinikoPatient.lastName || null,
                patientEmail: selectedClinikoPatient.email || null,
            };
            meta = {
                integrationSource: 'cliniko',
                cliniko: {
                    patientId: selectedClinikoPatient.clinikoPatientId || null,
                    bookingId: selectedClinikoAppointment?.id || null,
                    importToCetra: importClinikoPatient,
                    patientEmail: selectedClinikoPatient.email || null,
                    patientName: selectedClinikoPatient.firstName || null,
                    patientSurname: selectedClinikoPatient.lastName || null,
                },
            };
        }

        onSubmit(patientDetails, meta);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            handleClose();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            handleClose();
        });
    }, [open, handleClose]);

    const isSubmitDisabled = () => {
        if (inputMethod === 'manual') {
            return !manualPatientName && !manualPatientSurname;
        }
        if (inputMethod === 'select') {
            return !selectedPatient;
        }
        if (inputMethod === 'cliniko') {
            return !selectedClinikoPatient || !selectedClinikoAppointment;
        }
        return true;
    };

    const isBlocked = isAuthExpiredDialogVisible();

    return (
        <>
            <Dialog open={open && !isBlocked} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Patient Details</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Add patient details to personalise the shared handout.
                        </Typography>

                        <FormControl component="fieldset" sx={{ mb: 3 }}>
                            <RadioGroup value={inputMethod} onChange={(e) => setInputMethod(e.target.value)}>
                                <FormControlLabel value="manual" control={<Radio />} label="Enter patient details" />
                                <FormControlLabel
                                    value="select"
                                    control={<Radio />}
                                    label="Select existing patient"
                                    disabled={isLoadingPatients || patients.length === 0}
                                />
                                {showClinikoOption && (
                                    <FormControlLabel
                                        value="cliniko"
                                        control={<Radio />}
                                        label="Choose a Cliniko patient"
                                    />
                                )}
                            </RadioGroup>
                        </FormControl>

                        {inputMethod === 'manual' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Patient Name"
                                    value={manualPatientName}
                                    onChange={(e) => setManualPatientName(e.target.value)}
                                    fullWidth
                                    placeholder="e.g., Bella"
                                />
                                {/* <TextField
                                    label="Keeper Surname"
                                    value={manualClientSurname}
                                    onChange={(e) => setManualClientSurname(e.target.value)}
                                    fullWidth
                                    placeholder="e.g., Smith"
                                /> */}
                            </Box>
                        )}

                        {inputMethod === 'select' && (
                            <Autocomplete
                                options={patients}
                                getOptionLabel={(option) =>
                                    `${option.patientName || ''} - ${option.patientSurname || ''} ${
                                        option.clinicPatientId ? `(${option.clinicPatientId})` : ''
                                    }`.trim()
                                }
                                value={selectedPatient}
                                onChange={(event, newValue) => setSelectedPatient(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Patient" placeholder="Search patients..." />
                                )}
                                fullWidth
                                disabled={isLoadingPatients || patients.length === 0}
                            />
                        )}

                        {inputMethod === 'cliniko' && showClinikoOption && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    options={selectedClinikoPatient ? [selectedClinikoPatient] : []}
                                    getOptionLabel={(option) =>
                                        `${option?.firstName || ''} ${option?.lastName || ''} ${
                                            option?.email ? `(${option.email})` : ''
                                        }`.trim()
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?.clinikoPatientId === value?.clinikoPatientId
                                    }
                                    value={selectedClinikoPatient}
                                    onChange={(event, newValue) => setSelectedClinikoPatient(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Cliniko patient"
                                            placeholder="Select Cliniko patient"
                                        />
                                    )}
                                    fullWidth
                                    disabled={true}
                                />

                                <Autocomplete
                                    options={Array.isArray(clinikoAppointments) ? clinikoAppointments : []}
                                    getOptionLabel={(option) => {
                                        const start =
                                            option?.starts_at || option?.appointment_start || option?.startsAt || '';
                                        const end = option?.ends_at || option?.appointment_end || option?.endsAt || '';
                                        if (!start || !end) {
                                            return 'Unknown';
                                        }
                                        const startDate = new Date(start);
                                        const endDate = new Date(end);
                                        const dateLabel = startDate.toLocaleDateString('en-GB');
                                        const startTime = startDate.toLocaleTimeString('en-GB', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false,
                                        });
                                        const endTime = endDate.toLocaleTimeString('en-GB', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false,
                                        });
                                        return `${dateLabel}, ${startTime}-${endTime}`;
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    value={selectedClinikoAppointment}
                                    onChange={(event, newValue) => setSelectedClinikoAppointment(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select appointment"
                                            placeholder="Choose appointment for note"
                                            InputProps={{
                                                ...params.InputProps,
                                                readOnly: true,
                                            }}
                                        />
                                    )}
                                    fullWidth
                                    disabled={clinikoLoading || !Array.isArray(clinikoAppointments)}
                                />

                                {/* <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={importClinikoPatient}
                                            onChange={(e) => setImportClinikoPatient(e.target.checked)}
                                        />
                                    }
                                    label="Import patient into Cetra"
                                /> */}

                                {!clinikoLoading &&
                                    Array.isArray(clinikoAppointments) &&
                                    clinikoAppointments.length === 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 1, display: 'block' }}
                                        >
                                            No appointments found for this patient. Choose a different option to
                                            continue.
                                        </Typography>
                                    )}
                            </Box>
                        )}

                        {!isLoadingPatients && patients.length === 0 && !showClinikoOption && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                No patients found. Use manual entry to add patient details.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled()}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <BlockingSpinner open={open && !isBlocked && (isLoadingPatients || clinikoLoading)} />
        </>
    );
}
